import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import { Close, AutoAwesome, OpenInNew } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";
import { renderTemplateToHtml } from "../../../components/EmailBuilder";

interface Template {
  id: string;
  name: string;
  subject?: string;
  bodyHtml?: string;
  bodyDocument?: any;
  senderProfileId?: string | null;
  listId?: string | null;
  fieldMappings?: any;
}

interface Props {
  open: boolean;
  onClose: () => void;
  content: any;
  companyId: string;
  getToken: () => Promise<string>;
  onError: (error: string) => void;
  onSuccess?: (campaign: any) => void;
}

const PublishAsMarketingCampaignDialog: React.FC<Props> = ({
  open,
  onClose,
  content,
  companyId,
  getToken,
  onError,
  onSuccess,
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateId, setTemplateId] = useState<string>("");
  const [campaignName, setCampaignName] = useState<string>("");
  const [preview, setPreview] = useState<{
    bodyHtml: string;
    bodyDocument: any;
  } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setCampaignName(content?.title || "");
    const mc = (content?.variants || {}).marketingCampaign || {};
    setTemplateId(mc.templateId || "");
    (async () => {
      setLoadingTemplates(true);
      try {
        const token = await getToken();
        const res = await axios.get(
          `${hostname}/marketing-campaign-templates`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setTemplates(res.data?.items || []);
      } catch (err) {
        onError(parseError(err));
      } finally {
        setLoadingTemplates(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId, content?.id]);

  const runPreview = useCallback(async () => {
    if (!templateId) {
      onError("Pick a template first.");
      return;
    }
    setPreviewing(true);
    try {
      const token = await getToken();
      const res = await axios.post(
        `${hostname}/content/${content.id}/preview-campaign-merge`,
        { companyId, templateId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // The backend returns the merged Visual Builder JSON in
      // {subject, subTitle, content} shape. Render it to HTML client-side
      // using the same path the visual builder uses when saving a campaign.
      const bodyDocument = res.data.bodyDocument;
      let bodyHtml = "";
      try {
        bodyHtml = renderTemplateToHtml({
          subject: bodyDocument?.subject || "",
          subTitle: bodyDocument?.subTitle || "",
          content: bodyDocument?.content,
        });
      } catch (renderErr) {
        console.error("renderTemplateToHtml failed", renderErr);
      }
      setPreview({ bodyHtml, bodyDocument });
    } catch (err) {
      onError(parseError(err));
    } finally {
      setPreviewing(false);
    }
  }, [templateId, companyId, content?.id, getToken, onError]);

  const handlePublish = useCallback(async () => {
    if (!templateId) {
      onError("Pick a template first.");
      return;
    }
    setPublishing(true);
    try {
      const token = await getToken();
      const payload: any = {
        companyId,
        templateId,
        name: campaignName || content?.title,
      };
      // If the user already ran a preview, reuse that merged output so we
      // don't spend another LLM call on publish.
      if (preview?.bodyDocument) {
        payload.bodyDocument = preview.bodyDocument;
        payload.bodyHtml = preview.bodyHtml;
      }
      const res = await axios.post(
        `${hostname}/content/${content.id}/publish-as-campaign`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onSuccess) onSuccess(res.data?.campaign);
      onClose();
      if (res.data?.campaign?.id) {
        navigate(
          `/dashboard/companies/${companyId}/marketing-campaigns?campaignId=${res.data.campaign.id}`
        );
      }
    } catch (err) {
      onError(parseError(err));
    } finally {
      setPublishing(false);
    }
  }, [
    templateId,
    campaignName,
    content?.id,
    content?.title,
    companyId,
    getToken,
    onError,
    onSuccess,
    onClose,
    navigate,
    preview,
  ]);

  const selectedTemplate = templates.find((t) => t.id === templateId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Publish as Marketing Campaign
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" icon={<AutoAwesome />}>
            We'll use AI to weave this content's body into the selected
            template's layout and create a new draft marketing campaign.
          </Alert>

          <TextField
            label="Campaign Name"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            fullWidth
          />

          <FormControl fullWidth required disabled={loadingTemplates}>
            <InputLabel>Template</InputLabel>
            <Select
              label="Template"
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value as string);
                setPreview(null);
              }}
            >
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
              {templates.length === 0 && !loadingTemplates && (
                <MenuItem disabled value="">
                  No templates found — create one in Marketing Campaigns →
                  Templates
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", gap: 1 }}>
            <LoadingButton
              variant="outlined"
              onClick={runPreview}
              disabled={!templateId}
              loading={previewing}
              startIcon={<AutoAwesome />}
            >
              Preview AI Merge
            </LoadingButton>
            {selectedTemplate && (
              <Button
                variant="text"
                startIcon={<OpenInNew />}
                onClick={() =>
                  window.open(
                    `/dashboard/companies/${companyId}/marketing-campaigns?tab=templates`,
                    "_blank"
                  )
                }
              >
                Open template
              </Button>
            )}
          </Box>

          {preview && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                AI-merged preview
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
                Subject: {preview.bodyDocument?.subject}
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  backgroundColor: "action.hover",
                  borderRadius: 1,
                  maxHeight: 400,
                  overflow: "auto",
                }}
              >
                <div
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: preview.bodyHtml || "" }}
                />
              </Box>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          onClick={handlePublish}
          disabled={!templateId}
          loading={publishing}
        >
          Publish Campaign
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PublishAsMarketingCampaignDialog;
